<!-- src/views/OnBoarding/YourDetailsFormView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30 text-left">
    <FormHeader :mode="mode" />
    
    <div class="p-4 sm:p-6 md:p-8">
      <div class="max-w-7xl mx-auto pb-24">
        
        <!-- 1. MASTER COMMAND HEADER -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-8 md:p-12 border-b-4 border-[#20FCF1] shadow-2xl relative overflow-hidden border-x border-t border-slate-800">
          <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <ShieldCheckIcon class="h-32 w-32 text-white" />
          </div>
          <div class="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 class="text-3xl md:text-4xl font-black text-white uppercase tracking-tight leading-none text-left">{{ formTitle }}</h1>
              <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.2em] mt-4 text-left">
                Identity Registry & MFA Protocol • Phase 18 Midnight Premium
              </p>
            </div>

            <!-- Header Action Controls -->
            <div class="flex items-center gap-4 flex-shrink-0 self-start md:self-auto">
              <!-- View Template Button (Admins only on enrolment form) -->
              <button 
                v-if="mode === 'adminCreate'" 
                type="button" 
                @click="showTemplatePreview = true"
                class="px-6 py-3 bg-[#20FCF1] text-[#0A1630] rounded-2xl text-[10px] font-black uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-lg flex items-center gap-2 border-none"
              >
                <EnvelopeIcon class="h-4 w-4" /> View Email Template
              </button>

              <div v-if="submissionRecordExists" class="bg-white/5 px-6 py-3 rounded-2xl border border-white/10 flex items-center gap-3 backdrop-blur-md">
                <div class="w-2 h-2 bg-[#20FCF1] rounded-full animate-pulse"></div>
                <span class="text-white text-[10px] font-black uppercase tracking-widest text-left">Security Handshake Active</span>
              </div>
            </div>
          </div>
        </div>

        <div class="bg-white p-6 sm:p-10 md:p-16 rounded-b-[40px] shadow-2xl border-x border-b border-slate-100 text-left">

          <!-- Dynamic Instructions -->
          <section v-if="isLoadingText || (mainTextContent && mainTextContent !== '<p><br></p>')" class="ql-container mb-16 p-10 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner text-left">
            <div v-if="isLoadingText" class="flex items-center gap-3 text-left">
               <div class="animate-spin h-4 w-4 border-2 border-[#20FCF1] border-t-transparent rounded-full"></div>
               <span class="text-[10px] font-black uppercase text-slate-400 tracking-widest text-left">Initialising Instructions...</span>
            </div>
            <div v-else class="ql-editor !p-0 text-slate-600 leading-relaxed font-medium text-left" v-html="mainTextContent"></div>
          </section>

          <!-- Loading State -->
          <div v-if="isDataLoading" class="text-center py-24 text-left">
            <div class="animate-spin h-12 w-12 border-4 border-[#0A1630] border-t-[#20FCF1] rounded-full mx-auto mb-6 text-center"></div>
            <p class="text-[10px] font-black uppercase tracking-widest text-slate-400 text-center">Synchronising Registry Profile...</p>
          </div>

          <!-- SUCCESS STATE: ENROLMENT INVITE (Admin Mode Only) -->
          <div v-else-if="newUserPassword || subContractorPublicLink" class="p-10 md:p-16 bg-[#EBFDF8] rounded-[40px] border-2 border-[#20FCF1]/20 shadow-inner animate-fadeIn text-center">
            <CheckBadgeIcon class="h-20 w-20 text-[#20FCF1] mx-auto mb-6 text-center" />
            <h2 class="text-3xl font-black text-[#0A1630] uppercase tracking-tight text-center">
                {{ subContractorPublicLink ? 'Public Registration Initialised' : 'Member Enrolled Successfully' }}
            </h2>
            <p class="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] mt-3 text-center">Registry credentials generated and ready for distribution</p>

            <div class="max-w-4xl mx-auto bg-white rounded-[32px] border border-slate-200 shadow-2xl overflow-hidden my-12 text-left">
              <div class="bg-[#0A1630] px-10 py-5 flex items-center justify-between text-white border-b border-white/10 text-left">
                <div class="flex items-center gap-3 text-left">
                    <EnvelopeIcon class="h-5 w-5 text-[#20FCF1]" />
                    <span class="text-[10px] font-black text-white uppercase tracking-widest text-left">
                        {{ subContractorPublicLink ? 'Public Onboarding Link' : 'Access Authorisation Template' }}
                    </span>
                </div>
                <button @click="copyEmailTemplate" class="flex items-center gap-2 px-6 py-2 bg-white/10 text-[#20FCF1] rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all text-center">
                  <DocumentDuplicateIcon class="h-4 w-4" /> {{ emailCopyButtonText }}
                </button>
              </div>
              <div class="p-12 text-left">
                <pre class="whitespace-pre-wrap font-sans text-sm text-slate-600 leading-relaxed text-left">{{ emailBodyText }}</pre>
              </div>
            </div>

            <div class="flex flex-col sm:flex-row justify-center items-center gap-6 mt-10 text-center">
              <button @click="sendLoginEmail" class="px-12 py-5 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 transition-all shadow-xl flex items-center gap-3 text-center">
                 Launch Mail Client
              </button>
              <button @click="resetForNewUser" class="px-12 py-5 bg-white border-2 border-slate-100 text-slate-400 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:border-[#20FCF1] hover:text-[#0A1630] transition-all text-center">
                Add Another
              </button>
            </div>
          </div>

          <!-- Restricted Access Block -->
          <div v-if="permissionDenied" class="text-center py-20 bg-rose-50 rounded-[40px] border border-[#EF4444]/20 px-8 animate-fadeIn text-center">
             <ExclamationTriangleIcon class="h-16 w-16 text-[#EF4444] mx-auto mb-6 text-center" />
             <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-center">Access Restricted</h2>
             <p class="text-[#EF4444] text-[10px] font-black uppercase tracking-widest mt-2 text-center">Clearance required for this protocol</p>
             <button @click="$router.push('/dashboard')" class="mt-10 px-10 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest shadow-xl mx-auto block text-center">Return to Dashboard</button>
          </div>

          <!-- MAIN REGISTRY FORM -->
          <form v-else @submit.prevent="submitForm" class="space-y-20 animate-fadeIn text-left">
            
            <!-- SECTION 1: CORE IDENTITY -->
            <section class="text-left">
              <div class="flex items-center gap-4 mb-10 text-left">
                <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#20FCF1] text-left">
                  <IdentificationIcon class="h-7 w-7" />
                </div>
                <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">1. Core Identity & Classification</h2>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner text-left">
                <div v-if="isAdminEdit" class="md:col-span-2 p-8 bg-white rounded-[32px] border border-slate-200 shadow-sm text-left">
                  <label class="form-label text-[#20FCF1] text-left">Employment Classification</label>
                  <select v-model="formData.employmentType" required class="form-input font-black uppercase tracking-tight text-left">
                      <option disabled value="">-- Select Classification --</option>
                      <option v-for="option in employmentTypeOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                  </select>
                </div>

                <template v-if="isSubContractorSelection">
                  <div class="md:col-span-2 space-y-8 animate-fadeIn text-left">
                    <div class="p-6 bg-blue-50 border border-blue-100 rounded-2xl text-left">
                      <p class="text-[10px] font-black text-blue-600 uppercase tracking-widest flex items-center gap-2 text-left">
                        <InformationCircleIcon class="h-4 w-4" /> Guest Protocol Active
                      </p>
                      <p class="text-xs text-blue-500 font-medium mt-1 text-left">External entities use a public registration link. No app account is required.</p>
                    </div>
                    <div class="text-left">
                      <label class="form-label font-black text-[10px] uppercase text-left">Registered Company Name *</label>
                      <input type="text" v-model="formData.companyName" required class="form-input text-xl font-black uppercase text-left" placeholder="Enter Full Legal Entity Name" />
                    </div>
                    <div class="text-left">
                      <label class="form-label font-black text-[10px] uppercase text-left">Primary Contact Email *</label>
                      <input type="email" v-model="formData.email" required class="form-input font-black text-left" placeholder="e.g. compliance@company.co.uk" />
                    </div>
                  </div>
                </template>

                <template v-else>
                  <div v-if="isAdminEdit" class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-10 mb-6 pb-10 border-b border-slate-200 animate-fadeIn text-left">
                      <div class="space-y-8 text-left">
                        <div class="text-left">
                          <label class="form-label text-[#20FCF1] text-left">System Authorisation Tier</label>
                          <select v-model="selectedRoleId" :disabled="isSelfEdit && mode !== 'adminCreate'" class="form-input font-black uppercase tracking-tight text-left">
                              <option v-for="role in filteredRoles" :key="role.id" :value="role.id">{{ role.name }}</option>
                          </select>
                        </div>
                        <div class="text-left">
                          <label class="form-label font-black text-[10px] uppercase text-left">Designated Job Title</label>
                          <select v-model="formData.jobTitle" class="form-input font-bold uppercase text-left">
                            <option disabled value="">Select Title</option>
                            <option v-for="option in jobTitleOptions" :key="option.value" :value="option.value">{{ option.text }}</option>
                          </select>
                        </div>
                        
                        <!-- 🛡️ Sentinel Sponsorship Arrangement Selector placed symmetrically in Left Column -->
                        <div v-if="mode === 'adminCreate'" class="text-left animate-fadeIn">
                          <label class="form-label font-black text-[10px] uppercase text-[#20FCF1] text-left">
                            Sentinel Sponsorship Arrangement *
                          </label>
                          <select 
                            v-model="formData.sponsorshipType" 
                            required 
                            class="form-input font-black uppercase text-left border-[#20FCF1]/30 focus:border-[#20FCF1]"
                          >
                            <option disabled value="">Select Arrangement</option>
                            <option v-for="opt in sponsorshipTypeOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
                          </select>
                        </div>
                      </div>

                      <div class="space-y-8 text-left">
                        <div class="text-left">
                          <label class="form-label text-[#20FCF1] font-black text-[10px] uppercase tracking-widest text-left">Assigned Department(s)</label>
                          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm max-h-[180px] overflow-y-auto custom-scrollbar text-left">
                            <label v-for="option in departmentOptions" :key="option.value" class="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group text-left">
                              <input type="checkbox" :value="option.value" v-model="formData.departments" class="h-5 w-5 rounded border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1] text-left"/>
                              <span class="text-[10px] font-black uppercase text-slate-500 group-hover:text-[#0A1630] transition-colors text-left">{{ option.text }}</span>
                            </label>
                          </div>
                        </div>

                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left">
                          <div class="flex items-center justify-between p-5 bg-white rounded-3xl border border-slate-200 shadow-sm group text-left">
                              <div class="text-left">
                                  <p class="text-[10px] font-black uppercase text-[#0A1630] tracking-tight group-hover:text-[#FD5E00] transition-colors leading-none text-left">Dept. Manager</p>
                                  <p class="text-[8px] text-slate-400 font-bold uppercase mt-1.5 leading-tight text-left">Authorises timesheets</p>
                              </div>
                              <input type="checkbox" v-model="formData.isDepartmentManager" class="h-6 w-6 rounded-lg border-slate-300 text-[#20FCF1] focus:ring-[#20FCF1] cursor-pointer text-left" />
                          </div>

                          <div class="flex items-center justify-between p-5 bg-white rounded-3xl border border-slate-200 shadow-sm group text-left">
                              <div class="text-left">
                                  <p class="text-[10px] font-black uppercase text-[#0A1630] tracking-tight group-hover:text-amber-500 transition-colors leading-none text-left">Dept. Director</p>
                                  <p class="text-[8px] text-slate-400 font-bold uppercase mt-1.5 leading-tight text-left">Hierarchy Fallback</p>
                              </div>
                              <input type="checkbox" v-model="formData.isDepartmentDirector" class="h-6 w-6 rounded-lg border-slate-300 text-amber-500 focus:ring-amber-500 cursor-pointer text-left" />
                          </div>
                        </div>
                      </div>
                  </div>

                  <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                    <div class="space-y-8 text-left">
                      <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Legal First Name *</label><input type="text" v-model="formData.firstName" required class="form-input text-left" :readonly="!isAdminEdit" :class="{ 'readonly-input': !isAdminEdit }" /></div>
                      <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Legal Last Name *</label><input type="text" v-model="formData.lastName" required class="form-input text-left" :readonly="!isAdminEdit" :class="{ 'readonly-input': !isAdminEdit }" /></div>
                    </div>
                    <div class="space-y-8 text-left">
                      <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Primary Email Address *</label><input type="email" v-model="formData.email" required class="form-input font-black text-left" :readonly="mode === 'adminEdit'" :class="{ 'readonly-input': mode === 'adminEdit' }" /></div>
                      
                      <div v-if="mode === 'adminCreate'" class="text-left">
                        <label class="form-label font-black text-[10px] uppercase text-[#20FCF1] text-left flex items-center justify-between">
                            Target Mobile No. (MFA Handshake)
                            <span class="text-[#EF4444] text-[8px] font-black tracking-widest animate-pulse">Required for Access</span>
                        </label>
                        <input 
                            type="tel" 
                            v-model="formData.mobilePhoneNumber" 
                            required 
                            class="form-input font-black text-left border-[#20FCF1]/30 focus:border-[#20FCF1]" 
                            placeholder="e.g. 07700 900123" 
                        />
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </section>

            <!-- SECTION 2: PERSONAL REGISTRY & MFA -->
            <section v-if="!isSubContractorSelection && mode !== 'adminCreate'" class="text-left">
                <div class="flex items-center gap-4 mb-10 text-left">
                  <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#20FCF1] text-left">
                    <UserIcon class="h-7 w-7" />
                  </div>
                  <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">2. Personal Registry & Security Handshake</h2>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-2 gap-12 text-left">
                    <div class="bg-slate-50 p-10 rounded-[40px] border border-slate-100 shadow-inner flex flex-col items-center text-center">
                        <label class="form-label self-start mb-6 font-black uppercase text-[10px] text-left">Identity Verification Photo *</label>
                        <img v-if="photoPreviewUrl" :src="photoPreviewUrl" alt="Identity" class="h-48 w-48 rounded-full object-cover border-4 border-white shadow-2xl" />
                        <div v-else class="relative h-48 w-48 rounded-full bg-white border-2 border-dashed border-slate-200 flex items-center justify-center text-center"><PhotoIcon class="h-12 w-12 text-slate-200" /></div>
                        <input type="file" id="p_photo" accept="image/*" @change="handlePhotoUpload" class="hidden text-left" />
                        <button type="button" @click="$el.querySelector('#p_photo').click()" class="mt-8 px-8 py-3 bg-[#0A1630] text-white rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 shadow-lg text-center">Upload Photo</button>
                         
                        <!-- 🛡️ MANDATORY IDENTITY PHOTO GATING WARNING -->
                        <p class="mt-5 text-[9px] font-black uppercase tracking-wider text-[#EF4444] leading-relaxed max-w-xs text-center">
                        ⚠️ MANDATORY SPECIFICATION: Photo must be a clear, front-facing, full-face portrait. Avoid hats, sunglasses, or facial obstructions.
                        </p>
                    </div>

                    <div class="space-y-8 text-left">
                        <div class="grid grid-cols-1 gap-6 text-left">
                          <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Known As</label><input type="text" v-model="formData.knownAs" class="form-input text-left" /></div>
                          
                          <div class="text-left">
                            <label class="form-label font-black text-[10px] uppercase text-left">Contact No. (MFA Target) *</label>
                            <input 
                              type="tel" 
                              v-model="formData.mobilePhoneNumber" 
                              required 
                              placeholder="e.g. 07700 900123"
                              class="form-input transition-all text-left border-slate-200 focus:border-[#20FCF1]" 
                            />
                          </div>
                        </div>
                        
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Date of Birth *</label><input type="date" v-model="formData.dateOfBirth" required class="form-input text-left" /></div>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Residential Address *</label><input type="text" v-model="formData.addressLine1" required class="form-input text-left" /></div>
                        <div class="grid grid-cols-2 gap-6 text-left">
                          <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Town / City *</label><input type="text" v-model="formData.townOrCity" required class="form-input text-left" /></div>
                          <div class="text-left"><label class="form-label font-black text-[#0A1630] uppercase text-left">Post Code *</label><input type="text" v-model="formData.postCode" required class="form-input text-left" /></div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- SECTION 3: MEDICAL INDEX -->
            <section v-if="!isSubContractorSelection && mode !== 'adminCreate'" class="text-left">
                <div class="flex items-center gap-4 mb-10 text-left"><div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#20FCF1] text-left"><HeartIcon class="h-7 w-7" /></div><h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">3. Medical Index</h2></div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner text-left">
                    <div class="space-y-8 text-left">
                        <h3 class="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] border-b border-slate-200 pb-3 leading-none text-left">Practitioner Records</h3>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">NI Number *</label><input type="text" v-model="formData.niNumber" required class="form-input font-mono uppercase text-left" placeholder="XX 00 00 00 X" /></div>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">GP Full Name *</label><input type="text" v-model="formData.gpName" required class="form-input text-left" /></div>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Surgery Address *</label><textarea v-model="formData.gpAddress" required rows="3" class="form-input text-left"></textarea></div>
                    </div>
                    <div class="space-y-8 text-left">
                        <h3 class="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] border-b border-slate-200 pb-3 leading-none text-left">Emergency Contact (NOK)</h3>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">NOK Contact Name *</label><input type="text" v-model="formData.nextOfKinName" required class="form-input text-left" /></div>
                        <div class="grid grid-cols-2 gap-6 text-left">
                          <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Relationship *</label><input type="text" v-model="formData.nextOfKinRelationship" required class="form-input text-left" /></div>
                          <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Emergency No. *</label><input type="tel" v-model="formData.nextOfKinPhoneNumber" required class="form-input text-left" /></div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- SECTION 4: VEHICLE INDEX -->
            <section v-if="!isSubContractorSelection && mode !== 'adminCreate'" class="text-left">
                <div class="flex items-center gap-4 mb-10 text-left">
                    <div class="bg-[#0A1630] text-[#EAB308] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#EAB308] text-left"><TruckIcon class="h-7 w-7" /></div>
                    <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">4. Vehicle & Fleet Index</h2>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner text-left">
                    <div class="space-y-8 text-left">
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Driving Licence No.</label><input type="text" v-model="formData.drivingLicenceNumber" class="form-input font-mono uppercase text-left" placeholder="SMITH805214AB9IJ" /></div>
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Licence Expiry</label><input type="date" v-model="formData.drivingLicenceExpiry" class="form-input text-left" /></div>
                    </div>
                    <div class="space-y-8 text-left">
                        <div v-if="isAdminEdit" class="p-8 bg-white rounded-[32px] border border-slate-200 shadow-sm h-fit text-left">
                            <label class="form-label text-[#EAB308] font-black text-[10px] uppercase text-left">Assigned Vehicle Registration</label>
                            <input type="text" v-model="formData.assignedVehicleReg" placeholder="e.g. BD51 SMR" class="form-input font-black uppercase text-xl text-left" :readonly="!isAdminEdit" :class="{ 'bg-slate-50 cursor-not-allowed': !isAdminEdit }" />
                        </div>
                    </div>
                </div>
            </section>

            <!-- SECTION 5: WORK & FINANCIAL INDEX -->
            <section v-if="!isSubContractorSelection && mode !== 'adminCreate'" class="text-left">
                <div class="flex items-center gap-4 mb-10 text-left">
                    <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#20FCF1] text-left"><BriefcaseIcon class="h-7 w-7" /></div>
                    <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">5. Work & Financial Index</h2>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-10 bg-slate-50 p-10 rounded-[32px] border border-slate-100 shadow-inner text-left">
                    <div class="space-y-8 text-left">
                        <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Date Joined Company *</label><input type="date" v-model="formData.dateJoinedCompany" required class="form-input text-left" /></div>
                        <div class="grid grid-cols-2 gap-6 text-left">
                            <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Sentinel ID *</label><input type="text" v-model="formData.sentinelNumber" required class="form-input text-left" /></div>
                            <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Expiry Date *</label><input type="date" v-model="formData.sentinelExpiryDate" required class="form-input text-left" /></div>
                        </div>
                    </div>

                    <div v-if="isAdminEdit" class="md:col-span-2 p-10 bg-white rounded-[32px] border border-slate-200 shadow-sm space-y-8 text-left">
                        <h3 class="text-[11px] font-black uppercase text-[#20FCF1] tracking-[0.3em] leading-none text-left">Director Review: Payroll Rate Matrix</h3>
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-8 text-left">
                            <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">Basic (£)</label><input type="number" v-model.number="formData.basicHourlyRate" step="0.01" class="form-input text-xl font-black text-left" /></div>
                            <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">OT Hr (£)</label><input type="number" v-model.number="formData.overtimeHourlyRate" step="0.01" class="form-input text-xl font-black text-left" /></div>
                            <div class="text-left"><label class="form-label font-black text-[10px] uppercase text-left">OT Shift (£)</label><input type="number" v-model.number="formData.overtimeShiftRate" step="0.01" class="form-input text-xl font-black text-left" /></div>
                        </div>
                    </div>

                    <div class="md:col-span-2 pt-6 border-t border-slate-200 text-left">
                        <label class="form-label font-black text-[10px] uppercase text-left">Sentinel Sponsorship Arrangement *</label>
                        <select v-model="formData.sponsorshipType" required class="form-input font-bold uppercase text-left"><option v-for="opt in sponsorshipTypeOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option></select>
                    </div>
                </div>
            </section>

            <!-- SECTION 6: SIGNATURE SEAL -->
            <section v-if="mode === 'userEdit' && !isSubContractorSelection" class="text-left">
                <div class="flex items-center gap-4 mb-10 text-left">
                    <div class="bg-[#0A1630] text-[#20FCF1] w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-[#20FCF1] text-left"><PencilIcon class="h-7 w-7" /></div>
                    <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">6. Professional Signature Seal</h2>
                </div>
                <div class="bg-[#0A1630] p-10 rounded-[40px] text-white shadow-2xl relative overflow-hidden text-left">
                    <div class="absolute -left-10 -top-10 opacity-5 pointer-none"><ShieldCheckIcon class="w-64 h-64 text-[#20FCF1]" /></div>
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10 text-left">
                        <div class="bg-white rounded-[32px] p-6 text-center text-left">
                            <p class="micro-label mb-6 text-slate-400 uppercase leading-none text-center text-left">Draw Digital Seal Below</p>
                            <Vue3Signature ref="signaturePad" :sigOption="signatureOptions" :h="'220px'" :w="'100%'" class="border-2 border-dashed border-slate-100 rounded-2xl text-left"></Vue3Signature>
                            <div class="mt-6 flex gap-4 text-center text-left">
                                <button type="button" @click="saveSignature" :disabled="uploadingSignature" class="flex-1 py-4 bg-[#20FCF1] text-[#0A1630] rounded-xl font-black uppercase text-[10px] tracking-widest hover:scale-105 transition-all shadow-xl disabled:opacity-50 text-center border-none">Confirm Hand-Sign</button>
                                <button type="button" @click="clearSignature" class="px-8 py-4 bg-slate-100 text-slate-500 rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-200 transition-all text-center">Clear</button>
                            </div>
                        </div>
                        <div class="flex flex-col items-center justify-center p-10 bg-white/5 border border-white/10 rounded-[32px] text-center text-left">
                            <p class="micro-label text-[#20FCF1] mb-8 leading-none uppercase text-center text-left">Registry Preview</p>
                            <div v-if="formData.signatureDataUrl || formData.signatureUrl" class="p-6 bg-white rounded-2xl shadow-2xl animate-fadeIn max-w-[300px] text-left">
                                <img :src="formData.signatureDataUrl || formData.signatureUrl" class="max-h-24 object-contain grayscale text-left" alt="Seal" />
                            </div>
                            <div v-else class="text-white/20 text-[10px] font-black uppercase tracking-widest italic text-center">
                                Awaiting Digital Hand-Sign
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <!-- 🛡️ SECTION 7: OPERATIONAL AVAILABILITY (ABSENCE PROTOCOL) -->
            <section v-if="isSelfEdit && (userStore.isDepartmentManager || userStore.isDepartmentDirector)" class="text-left animate-fadeIn">
                <div class="flex items-center gap-4 mb-10 text-left">
                    <div class="bg-[#0A1630] text-rose-400 w-14 h-14 rounded-2xl flex items-center justify-center shadow-xl border-b-2 border-rose-400 text-left">
                      <CalendarIcon class="h-7 w-7" />
                    </div>
                    <h2 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight text-left">7. Absence & Escalation Protocol</h2>
                </div>
                
                <div class="bg-[#0A1630] p-8 md:p-12 rounded-[40px] border border-white/10 shadow-2xl relative overflow-hidden text-left">
                  <div class="absolute right-0 bottom-0 p-8 opacity-5 pointer-events-none">
                    <ArrowPathIcon class="h-32 w-32 text-white" />
                  </div>
                  
                  <div class="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8 text-left">
                    <div class="max-w-xl text-left">
                      <h3 class="text-white font-black uppercase text-lg tracking-tight text-left">Departmental Availability</h3>
                      <p class="text-slate-400 text-xs font-medium mt-3 leading-relaxed text-left">
                        Activating <span class="text-rose-400 font-bold uppercase">Annual Leave</span> will automatically reroute all incoming departmental approval requests to the designated Director.
                      </p>
                    </div>
                    
                    <button 
                      type="button"
                      @click="toggleAbsenceStatus"
                      :disabled="isTogglingLeave"
                      :class="userStore.isOnLeave ? 'bg-[#EF4444] text-white shadow-rose-500/20' : 'bg-[#20FCF1] text-[#0A1630] shadow-[#20FCF1]/20'"
                      class="px-12 py-5 rounded-2xl font-black uppercase text-xs tracking-widest shadow-2xl transition-all hover:scale-105 active:scale-95 disabled:opacity-50 min-w-[240px] text-center"
                    >
                      <span v-if="isTogglingLeave" class="flex items-center justify-center gap-2 text-center">
                        <div class="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full text-center"></div>
                        Syncing Registry...
                      </span>
                      <span v-else class="text-center">
                        {{ userStore.isOnLeave ? 'Currently on Leave' : 'Mark as Active' }}
                      </span>
                    </button>
                  </div>
                </div>
            </section>

            <!-- SUBMISSION ENGINE -->
            <div class="pt-10 text-left">
              <button 
                type="submit" 
                :disabled="isSubmitting || !isFormReady" 
                class="btn-premium disabled:bg-slate-200 disabled:text-slate-400 disabled:scale-100 disabled:cursor-not-allowed text-center"
              >
                <span v-if="isSubmitting" class="flex items-center justify-center gap-4 text-center"><div class="animate-spin h-5 w-5 border-2 border-[#0A1630] border-t-transparent rounded-full text-center"></div> Synchronising Registry...</span>
                <span v-else class="text-center">{{ isSubContractorSelection && mode === 'adminCreate' ? 'Authorise Entity Registration' : submitButtonText }}</span>
              </button>
              
              <div v-if="error" class="mt-8 p-10 bg-rose-50 border-2 border-[#EF4444]/20 rounded-[40px] flex items-center gap-6 shadow-xl animate-fadeIn text-left">
                 <ExclamationTriangleIcon class="h-10 w-10 text-[#EF4444] flex-shrink-0" />
                 <div class="text-left">
                   <p class="text-[10px] font-black uppercase tracking-widest text-[#EF4444] leading-none text-left">Registry Synchronisation Error</p>
                   <p class="text-sm font-bold text-rose-700 mt-2 uppercase tracking-tight text-left">Handshake Failure: {{ error }}</p>
                 </div>
              </div>
            </div>
          </form>

          <!-- DANGEROUS ZONE: PURGE ENTITY (Admin Edit Mode Only) -->
          <section v-if="isAdminEdit && mode === 'adminEdit'" class="mt-20 pt-10 border-t-4 border-rose-100 animate-fadeIn text-left">
            <div class="bg-rose-50 border border-rose-200 rounded-[32px] p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
              <div class="max-w-xl text-left">
                <h3 class="text-[#EF4444] font-black uppercase text-lg tracking-tight leading-none text-left">Danger Zone: Purge Entity</h3>
                <p class="text-rose-700 text-xs font-medium mt-3 leading-relaxed text-left">
                  Permanently delete this member from the database, including their profile, timesheets, and onboarding submissions. This action is irreversible.
                </p>
              </div>
              
              <button 
                type="button"
                @click="handleDeleteUser"
                :disabled="isDeleting"
                class="bg-rose-600 text-white px-10 py-5 rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-rose-700 transition-all shadow-xl active:scale-95 disabled:opacity-50 min-w-[200px] text-center"
              >
                <span v-if="isDeleting" class="flex items-center justify-center gap-2 text-center">
                  <div class="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full text-center"></div>
                  Purging...
                </span>
                <span v-else class="text-center">Purge Entity</span>
              </button>
            </div>
          </section>

          <!-- Security Access Key Update -->
          <div v-if="mode === 'userEdit' && !isDataLoading && !isSubContractorSelection" class="mt-24 pt-24 border-t border-slate-100 text-left">
             <div class="bg-slate-50 p-10 rounded-[40px] border border-slate-100 shadow-sm max-w-4xl mx-auto text-left">
                <div class="flex items-center gap-3 mb-10 text-left"><KeyIcon class="h-6 w-6 text-[#20FCF1] text-left" /><h3 class="text-sm font-black uppercase tracking-[0.2em] text-[#0A1630] text-left">Security Access Key Update</h3></div>
                <form @submit.prevent="handlePasswordUpdate" class="space-y-8 text-left">
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-10 text-left">
                      <div class="text-left"><label class="form-label font-black text-[10px] uppercase tracking-widest text-slate-400 mb-2 ml-1">New Access Key</label><input type="password" v-model="newPasswordValue" required class="form-input text-left" /></div>
                      <div class="text-left"><label class="form-label font-black text-[10px] uppercase tracking-widest text-slate-400 mb-2 ml-1">Confirm Key</label><input type="password" v-model="confirmPasswordValue" required class="form-input text-left" /></div>
                    </div>
                    <div class="flex justify-end pt-6 text-left"><button type="submit" :disabled="isUpdatingPassword" class="px-12 py-4 bg-[#0A1630] text-white rounded-2xl font-black uppercase text-[10px] tracking-widest hover:bg-slate-800 shadow-xl transition-all text-center">Update Access Key</button></div>
                    <p v-if="passwordSuccess" class="text-[10px] text-[#20FCF1] font-black uppercase tracking-widest text-right mt-6 text-left">{{ passwordSuccess }}</p>
                </form>
             </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 🛡️ EMAIL INVITATION TEMPLATE PREVIEW MODAL -->
    <div v-if="showTemplatePreview" class="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A1630]/90 backdrop-blur-md px-4 animate-fadeIn">
      <div class="bg-white rounded-[40px] shadow-2xl w-full max-w-4xl p-10 md:p-12 border border-slate-100 text-left relative max-h-[90vh] overflow-y-auto custom-scrollbar">
        
        <button @click="showTemplatePreview = false" class="absolute top-6 right-6 text-slate-400 hover:text-[#EF4444] transition-colors border-none bg-transparent">
          <XMarkIcon class="h-8 w-8" />
        </button>

        <h3 class="text-2xl font-black text-[#0A1630] uppercase tracking-tight mb-4 flex items-center gap-3">
          <EnvelopeIcon class="h-6 w-6 text-[#20FCF1]" /> 
          Onboarding Invitation Registries
        </h3>
        <p class="text-slate-400 text-xs font-bold uppercase tracking-wider mb-8">
          Authorised communications for manual operative distribution
        </p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
          <!-- Column 1: Standard Operative Template -->
          <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex flex-col h-full justify-between">
            <div>
              <div class="flex justify-between items-center mb-4 border-b pb-3">
                <span class="text-[10px] font-black uppercase text-[#20FCF1] tracking-widest">1. Standard Operative Invite</span>
                <button @click="copyStaticTemplate('standard')" class="px-4 py-2 bg-[#0A1630] text-white hover:bg-[#20FCF1] hover:text-[#0A1630] rounded-lg text-[8px] font-black uppercase tracking-widest transition-all border-none">
                  {{ copiedStandardText }}
                </button>
              </div>
              <pre class="whitespace-pre-wrap font-sans text-xs text-slate-600 leading-relaxed max-h-[350px] overflow-y-auto custom-scrollbar">{{ getStaticTemplateText('standard') }}</pre>
            </div>
          </div>

          <!-- Column 2: Sub-Contractor Template -->
          <div class="bg-slate-50 p-6 rounded-3xl border border-slate-100 flex flex-col h-full justify-between">
            <div>
              <div class="flex justify-between items-center mb-4 border-b pb-3">
                <span class="text-[10px] font-black uppercase text-[#20FCF1] tracking-widest">2. Sub-Contractor Invite</span>
                <button @click="copyStaticTemplate('sub')" class="px-4 py-2 bg-[#0A1630] text-white hover:bg-[#20FCF1] hover:text-[#0A1630] rounded-lg text-[8px] font-black uppercase tracking-widest transition-all border-none">
                  {{ copiedSubText }}
                </button>
              </div>
              <pre class="whitespace-pre-wrap font-sans text-xs text-slate-600 leading-relaxed max-h-[350px] overflow-y-auto custom-scrollbar">{{ getStaticTemplateText('sub') }}</pre>
            </div>
          </div>
        </div>

        <div class="mt-10 pt-6 border-t border-slate-100 flex justify-end">
          <button @click="showTemplatePreview = false" class="px-8 py-4 bg-[#0A1630] text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-[#20FCF1] hover:text-[#0A1630] transition-all shadow-md border-none">
            Close Preview
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useUserStore } from '@/stores/userStore';
import { storage, db, firebaseApp, auth } from '@/firebaseConfig';
import { 
    updatePassword, EmailAuthProvider, reauthenticateWithCredential
} from 'firebase/auth';
import { ref as storageRefValue, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, getDoc, setDoc, collection, query, where, getDocs, serverTimestamp, addDoc } from 'firebase/firestore'; 
import { useRouter, useRoute } from 'vue-router';
import Vue3Signature from 'vue3-signature';
import FormHeader from '@/components/FormHeader.vue';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { 
  IdentificationIcon, CheckBadgeIcon, UserIcon, PhotoIcon, 
  PencilIcon, HeartIcon, DocumentDuplicateIcon, EnvelopeIcon, KeyIcon,
  ExclamationTriangleIcon, ShieldCheckIcon, TruckIcon, InformationCircleIcon, BriefcaseIcon,
  CalendarIcon, ArrowPathIcon, XMarkIcon
} from '@heroicons/vue/24/outline';

const functions = getFunctions(firebaseApp, 'europe-west2');
const router = useRouter();
const route = useRoute();
const userStore = useUserStore();

const props = defineProps({ userId: { type: String, default: null } });

const mode = ref('loading');
const isDataLoading = ref(true);
const permissionDenied = ref(false); 
const formData = ref({});
const photoPreviewUrl = ref('');
const error = ref('');
const uploadingPhoto = ref(false);
const photoUploadProgress = ref(0);
const uploadingSignature = ref(false);
const isSubmitting = ref(false);
const isTogglingLeave = ref(false);
const subContractorPublicLink = ref('');
const signaturePad = ref(null);
const signatureOptions = ref({ penColor: "#0A1630", backgroundColor: "rgba(255,255,255,0)" });

const newPasswordValue = ref('');
const confirmPasswordValue = ref('');
const isUpdatingPassword = ref(false);
const passwordSuccess = ref('');

const employmentTypeOptions = ref([]);
const jobTitleOptions = ref([]);
const departmentOptions = ref([]);
const sponsorshipTypeOptions = ref([]);
const submissionRecordExists = ref(false);
const newUserPassword = ref('');
const emailCopyButtonText = ref('Copy Template');
const allRoles = ref([]);
const selectedRoleId = ref(null);
const initializationStarted = ref(false);
const mainTextContent = ref('');
const isLoadingText = ref(true);

const isDeleting = ref(false);

const showTemplatePreview = ref(false);
const copiedStandardText = ref('Copy');
const copiedSubText = ref('Copy');

const isAdminEdit = computed(() => {
  return userStore.hasAdminAccess || userStore.permissions?.canManageUsers || userStore.role === 'super-admin' || userStore.userProfile?.role === 'super-admin';
});

const isSelfEdit = computed(() => auth.currentUser?.uid === formData.value?.userId);

const isSubContractorSelection = computed(() => {
  const type = String(formData.value.employmentType || '').toLowerCase().trim();
  return type.includes('sub') && type.includes('contract');
});

const filteredRoles = computed(() => {
  if (userStore.isAdmin) return allRoles.value;
  return allRoles.value.filter(r => String(r.name).toLowerCase() !== 'admin');
});

const formTitle = computed(() => mode.value === 'adminCreate' ? (isSubContractorSelection.value ? 'Initialise Public Registry' : 'Member Enrolment Protocol') : 'Identity Registry Protocol');

/**
 * 🛡️ AUTHORITATIVE EMAIL TEMPLATE
 * Realigned to HR requirements for manual distribution.
 */
const emailBodyText = computed(() => {
  if (isSubContractorSelection.value) {
    return `Dear ${formData.value.companyName || 'Personnel'},\n\nYour company entity has been successfully registered on Sentinel Safe. To remain compliant with our annual health and safety obligations, you are kindly requested to complete the mandatory "Sub-Contractor Pack" via the link below.\n\nPlease ensure this onboarding process is completed within 7 days of receiving this email, as this secure access link will expire after this period.\n\nThank you for your prompt cooperation in ensuring we remain fully compliant.\n\nAccess Link:\n${subContractorPublicLink.value}`;
  }
 
  return `Dear ${formData.value.firstName || 'Personnel'},\n\nYou have been successfully registered on Sentinel Safe, our online compliance system. Please find below your login details, where you are required to complete your Annual Induction Forms via the Onboarding section.\n\nCompletion of the Onboarding process is a mandatory requirement under our annual compliance and health & safety obligations. All staff must complete the full onboarding process no later than 7 days from the date of this email.\n\nUpon first login attempt, you will be sent a 6 digit code via SMS. If you do not receive the SMS within 30 seconds, please check your Spam/Junk folder. If no SMS is received, you can use the email fallback feature. Once logged in, you will be required to reset your password via your profile page. Please ensure your new password is secure and kept confidential.\n\nOnce logged in, watch the short video showing you how to complete your induction. Once you have carefully reviewed and completed all sections, your submissions will be recorded securely within the system for compliance auditing purposes.\n\nIf you experience any technical issues, please notify me immediately so this can be resolved within the 7-day deadline.\n\nThank you for your prompt cooperation in ensuring we remain fully compliant.\n\nLogin Details\nWebsite: sentinel-safe.app/login\nEmail: ${formData.value.email || '[Insert email]'}\nTemporary Password: ${newUserPassword.value}`;
});

const submitButtonText = computed(() => mode.value === 'adminCreate' ? 'Authorise Enrolment' : 'Synchronise Profile');
const isFormReady = computed(() => !uploadingPhoto.value && (!!formData.value.userId || mode.value === 'adminCreate'));

const getStaticTemplateText = (type) => {
  if (type === 'sub') {
    return `Dear [Company Name],\n\nYour company entity has been successfully registered on Sentinel Safe. To remain compliant with our annual health and safety obligations, you are kindly requested to complete the mandatory "Sub-Contractor Pack" via the link below.\n\nPlease ensure this onboarding process is completed within 7 days of receiving this email, as this secure access link will expire after this period.\n\nThank you for your prompt cooperation in ensuring we remain fully compliant.\n\nAccess Link:\n[Secure Registration Link]`;
  }
  return `Dear [First Name],\n\nYou have been successfully registered on Sentinel Safe, our online compliance system. Please find below your login details, where you are required to complete your Annual Induction Forms via the Onboarding section.\n\nCompletion of the Onboarding process is a mandatory requirement under our annual compliance and health & safety obligations. All staff must complete the full onboarding process no later than 7 days from the date of this email.\n\nUpon first login attempt, you will be sent a 6 digit code via SMS. If you do not receive the SMS within 30 seconds, please check your Spam/Junk folder. If no SMS is received, you can use the email fallback feature. Once logged in, you will be required to reset your password via your profile page. Please ensure your new password is secure and kept confidential.\n\nOnce logged in, watch the short video showing you how to complete your induction. Once you have carefully reviewed and completed all sections, your submissions will be recorded securely within the system for compliance auditing purposes.\n\nIf you experience any technical issues, please notify me immediately so this can be resolved within the 7-day deadline.\n\nThank you for your prompt cooperation in ensuring we remain fully compliant.\n\nLogin Details\nWebsite: sentinel-safe.app/login\nEmail: [User Email]\nTemporary Password: [Temporary Password]`;
};

const copyStaticTemplate = async (type) => {
  const text = getStaticTemplateText(type);
  try {
    await navigator.clipboard.writeText(text);
    if (type === 'standard') {
      copiedStandardText.value = 'Copied!';
      setTimeout(() => {
        copiedStandardText.value = 'Copy';
      }, 3000);
    } else {
      copiedSubText.value = 'Copied!';
      setTimeout(() => {
        copiedSubText.value = 'Copy';
      }, 3000);
    }
  } catch (err) {
    console.warn("Clipboard copy failed:", err);
  }
};

const toggleAbsenceStatus = async () => {
  isTogglingLeave.value = true;
  try {
    const nextStatus = !userStore.isOnLeave;
    await userStore.updateAbsenceStatus(nextStatus);
    formData.value.isOnAnnualLeave = nextStatus;
  } catch (e) {
    console.warn("Absence sync fail:", e);
    error.value = "Failed to update absence registry.";
  } finally {
    isTogglingLeave.value = false;
  }
};

const handlePasswordUpdate = async () => {
  isUpdatingPassword.value = true;
  try {
    if (newPasswordValue.value !== confirmPasswordValue.value) throw new Error("Mismatch.");
    await updatePassword(auth.currentUser, newPasswordValue.value);
    passwordSuccess.value = 'Registry Key Updated.';
  } catch (e) {
    if (e.code === 'auth/requires-recent-login') {
      const cur = prompt("Verify current security key:");
      if (cur) {
        const credential = EmailAuthProvider.credential(auth.currentUser.email, cur);
        await reauthenticateWithCredential(credential);
        await updatePassword(auth.currentUser, newPasswordValue.value);
        passwordSuccess.value = 'Authorised & Updated.';
      }
    }
  } finally { isUpdatingPassword.value = false; }
};

const initializeNewUserForm = (clientId) => {
    formData.value = { 
      firstName: '', lastName: '', email: '', mobilePhoneNumber: '', 
      clientId, employmentType: '', jobTitle: '', departments: [], 
      isDepartmentManager: false, isDepartmentDirector: false, companyName: '', 
      basicHourlyRate: 0, overtimeHourlyRate: 0, overtimeShiftRate: 0, 
      assignedVehicleReg: '', drivingLicenceNumber: '', drivingLicenceExpiry: null, 
      sponsorshipType: '', 
      status: 'pending_onboarding' 
    };
    selectedRoleId.value = 'standard';
};

const uploadFileToStorage = (file, path) => new Promise((resolve, reject) => {
    const isPhoto = file === formData.value.photoFile;
    const task = uploadBytesResumable(storageRefValue(storage, path), file);
    if (isPhoto) uploadingPhoto.value = true;
    task.on('state_changed', s => { if (isPhoto) photoUploadProgress.value = (s.bytesTransferred/s.totalBytes)*100; }, e => reject(e), async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        if (isPhoto) uploadingPhoto.value = false;
        resolve(url);
    });
});

const loadUserProfile = async (userId) => {
    const snap = await getDoc(doc(db, 'userProfiles', userId));
    if (snap.exists()) {
        const data = snap.data();
        const depts = Array.isArray(data.departments) ? data.departments : (data.department ? [data.department] : []);
        formData.value = { ...data, userId: snap.id, departments: depts };
        if (data.photoUrl) photoPreviewUrl.value = data.photoUrl;
        selectedRoleId.value = formData.value.permissionRoleId || formData.value.role;
    }
};

const fetchFormOptions = async (cid) => {
  const ids = { employmentTypes: employmentTypeOptions, jobTitles: jobTitleOptions, sponsorshipTypes: sponsorshipTypeOptions, departments: departmentOptions };
  for (const [docId, refObj] of Object.entries(ids)) {
    const snap = await getDoc(doc(db, 'formOptions', docId));
    if (snap.exists()) { 
      const data = snap.data()[cid] || snap.data().global || [];
      refObj.value = data.sort((a,b) => String(a.text || '').localeCompare(String(b.text || ''))); 
    }
  }
};

const handleDeleteUser = async () => {
  const targetId = props.userId || route.params.userId;
  if (!targetId) {
    alert("Protocol Error: Identity UID not hydrated.");
    return;
  }

  if (!confirm("Authorise Entity Purge? This action is irreversible and will permanently delete the member from the registry.")) return;
  
  isDeleting.value = true;
  try {
    const deleteFunc = httpsCallable(functions, 'deleteClientUser');
    const res = await deleteFunc({ userIdToDelete: targetId });
    if (res.data?.success) {
        alert("Entity successfully purged.");
        router.push({ name: 'AdminPersonnelDashboard' });
    } else {
        throw new Error(res.data?.message || "Purge handshake failed.");
    }
  } catch (err) { 
      console.warn("Purge Protocol Failure:", err);
      alert("Purge Failure: " + err.message);
  } finally { 
      isDeleting.value = false; 
  }
};

const submitForm = async () => {
  isSubmitting.value = true; error.value = '';
  try {
    const cid = userStore.userProfile?.clientId || userStore.clientId || userStore.claims?.clientId;
    if (mode.value === 'adminCreate') {
       if (isSubContractorSelection.value) {
          const publicRegId = `sub_${Date.now()}`;
          const directoryStubId = `sub_directory_${Date.now()}`;
          await setDoc(doc(db, 'publicOnboardingRegistry', publicRegId), { clientId: cid, companyName: formData.value.companyName, email: formData.value.email, type: 'sub-contractor', targetProfileId: directoryStubId, createdAt: serverTimestamp(), active: true });
          subContractorPublicLink.value = `${window.location.origin}/public/onboarding/${publicRegId}`;
          await setDoc(doc(db, 'userProfiles', directoryStubId), { clientId: cid, firstName: formData.value.companyName, lastName: '(Entity)', email: formData.value.email, employmentType: 'sub-contractor', role: 'standard', status: 'pending_onboarding', createdAt: serverTimestamp() });
       } else {
          const primaryDept = formData.value.departments?.length > 0 ? formData.value.departments[0] : '';
          
          const authPayload = { 
            ...formData.value, 
            phoneNumber: formData.value.mobilePhoneNumber,
            mobilePhoneNumber: formData.value.mobilePhoneNumber,
            mfaPhoneNumber: formData.value.mobilePhoneNumber,
            role: selectedRoleId.value, 
            department: primaryDept 
          };

          const res = await httpsCallable(functions, 'createClientUser')(authPayload);
          
          if (res.data?.success && res.data.userId) {
              await setDoc(doc(db, 'userProfiles', res.data.userId), { 
                ...formData.value, 
                email: String(authPayload.email).toLowerCase().trim(), 
                isDepartmentManager: !!formData.value.isDepartmentManager, 
                isDepartmentDirector: !!formData.value.isDepartmentDirector, 
                departments: formData.value.departments || [], 
                department: primaryDept, 
                permissionRoleId: selectedRoleId.value, 
                role: selectedRoleId.value, 
                clientId: cid, 
                emailVerified: true, 
                status: 'pending_onboarding', 
                createdAt: serverTimestamp() 
              }, { merge: true });
              
              await httpsCallable(functions, 'setUserRoleAndPermissions')({ 
                userId: res.data.userId, 
                roleId: selectedRoleId.value, 
                additionalClaims: { 
                  isDepartmentManager: !!formData.value.isDepartmentManager, 
                  isDepartmentDirector: !!formData.value.isDepartmentDirector, 
                  departments: formData.value.departments || [], 
                  emailVerified: true 
                } 
              });
              
              newUserPassword.value = res.data.temporaryPassword;
          } else { throw new Error("Identity creation failed."); }
       }
    } else {
       if (formData.value.photoFile) { 
         formData.value.photoUrl = await uploadFileToStorage(formData.value.photoFile, `clients/${cid}/userProfiles/${formData.value.userId}/photo/${Date.now()}`); 
       }
       const clean = { ...formData.value }; 
       delete clean.photoFile; 
       delete clean.userId;
       if (clean.signatureDataUrl) clean.signatureUrl = ""; 

       await setDoc(doc(db, 'userProfiles', formData.value.userId), { ...clean, onboarding_yourDetails: true }, { merge: true });
       const submissionRef = await addDoc(collection(db, 'onboardingFormSubmissions'), { userId: formData.value.userId, formIdentifier: 'yourDetails', submittedAt: serverTimestamp(), clientName: userStore.userProfile?.clientName || 'TECS', status: 'completed' });
       httpsCallable(functions, 'evaluateUserCompliance')({ targetUserId: formData.value.userId }).catch(() => {});
       router.push({ path: '/dashboard', query: { submitted: 'yourDetails', ref: submissionRef.id } });
    }
  } catch (e) { error.value = e.message; } finally { isSubmitting.value = false; }
};

const initializeForm = async () => {
  if (initializationStarted.value) return; 
  initializationStarted.value = true;
  isDataLoading.value = true;
  permissionDenied.value = false;
  try {
    const cid = userStore.userProfile?.clientId || userStore.clientId || userStore.claims?.clientId;
    if (!cid) { initializationStarted.value = false; return; }
    mode.value = (route.name === 'AdminAddUser' || route.path.includes('/admin/users/new')) ? 'adminCreate' : props.userId ? 'adminEdit' : 'userEdit';
    const targetUserId = (props.userId && isAdminEdit.value) ? props.userId : auth.currentUser?.uid;
    const promises = [fetchFormOptions(cid)];
    if (isAdminEdit.value) {
      promises.push(
        httpsCallable(functions, 'getPermissionRoles')()
          .then(res => {
            allRoles.value = res.data || [];
          })
          .catch(err => {
            console.warn("Telemetry: getPermissionRoles bypassed or unauthorized. Falling back to default roles.", err);
            allRoles.value = [{ id: 'admin', name: 'Admin' }, { id: 'standard', name: 'Standard' }];
          })
      );
    }
    if (mode.value === 'adminCreate') initializeNewUserForm(cid);
    else if (targetUserId) {
      promises.push(loadUserProfile(targetUserId));
      promises.push(getDocs(query(collection(db, "onboardingFormSubmissions"), where("userId", "==", targetUserId), where("formIdentifier", "==", "yourDetails"))).then(s => submissionRecordExists.value = !s.empty));
    }
    const clientSnap = await getDoc(doc(db, 'clients', cid));
    if (clientSnap.exists()) mainTextContent.value = clientSnap.data().onboardingFormTexts?.yourDetails || '';
    await Promise.all(promises);
  } catch (e) { if (!props.userId) permissionDenied.value = true; } 
  finally { isLoadingText.value = false; isDataLoading.value = false; }
};

// Re-trigger form hydration once the user profile has finished loading from Firestore
watch(() => userStore.isUserProfileLoading, (isLoading) => {
  if (!isLoading && userStore.clientId) {
    initializationStarted.value = false;
    initializeForm();
  }
}, { immediate: true });

watch(() => userStore.userProfile?.clientId, (newCid) => {
  if (newCid) {
    initializationStarted.value = false;
    initializeForm();
  }
});

watch(() => props.userId, () => { 
  initializationStarted.value = false; 
  initializeForm(); 
});

const handlePhotoUpload = (e) => { const f = e.target.files[0]; if(f){ formData.value.photoFile = f; photoPreviewUrl.value = URL.createObjectURL(f); }};
const saveSignature = () => { if (signaturePad.value) { const b64 = signaturePad.value.save('image/png'); formData.value.signatureDataUrl = b64; formData.value.signatureUrl = ''; uploadingSignature.value = true; setTimeout(() => { uploadingSignature.value = false; }, 1000); }};
const clearSignature = () => { if (signaturePad.value) { signaturePad.value.clear(); formData.value.signatureDataUrl = ''; formData.value.signatureUrl = ''; } };
const resetForNewUser = () => { newUserPassword.value = ''; subContractorPublicLink.value = ''; initializationStarted.value = false; initializeForm(); };
const copyEmailTemplate = async () => { try { await navigator.clipboard.writeText(emailBodyText.value); emailCopyButtonText.value = 'Copied!'; setTimeout(() => { emailCopyButtonText.value = 'Copy Template'; }, 3000); } catch (err) { console.warn("Clipboard fail:", err); } };
const sendLoginEmail = () => { window.location.href = `mailto:${formData.value.email}?subject=Sentinel Safe Registration&body=${encodeURIComponent(emailBodyText.value)}`; };
</script>

<style scoped>
.form-input { @apply block w-full px-5 py-4 bg-white border border-slate-200 rounded-2xl text-[#0A1630] font-bold transition-all focus:ring-2 focus:ring-[#20FCF1] outline-none sm:text-sm text-left; }
.readonly-input { @apply bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed; }
.form-label { @apply block text-[11px] font-black text-[#0A1630] uppercase tracking-[0.2em] mb-3 ml-1 text-left; }
.btn-premium { @apply w-full bg-[#20FCF1] text-[#0A1630] py-7 rounded-[32px] text-xl font-black uppercase tracking-[0.2em] shadow-2xl hover:scale-[1.01] active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center; }
.micro-label { @apply block text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] text-left; }
.animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
:deep(.ql-editor) { font-size: 0.95rem; line-height: 1.8; color: #334155; text-align: left; }
.custom-scrollbar { scrollbar-width: thin; scrollbar-color: #20FCF1 transparent; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background: #20FCF1; border-radius: 10px; }
</style>